import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { JUST_STORES } from "../api/maps-queries";
import { GET_PICKUP_NOW_STORES } from "../../home/api/home-queries";
import { getMapPosition } from "../utils/map-position-storage";
import {
  filterByPickupTime,
  STORE_FILTER,
} from "../../home/utils/store-filter";
import type {
  JustStoresQuery,
  JustStoresQueryVariables,
  PickupNowStoresQuery,
  PickupNowStoresQueryVariables,
} from "../../../__generated__/graphql";
import type { Store } from "../../../__generated__/graphql";
import type { PickupTimeFilter } from "../../home/hooks/use-pickup-time-filter";

const DEFAULT_RADIUS_KM = 5;
/** 지도 마커용 바로픽업 매장 상한 (리스트 무한스크롤과 별도) */
const MAP_PICKUP_NOW_LIMIT = 120;

export type MapAvailableFilter = "ALL" | "AVAILABLE" | "PICKUP_NOW";

export interface UseMapStoresOptions {
  availableFilter: MapAvailableFilter;
  pickupTimeFilter: PickupTimeFilter | null;
}

export function useMapStores({
  availableFilter,
  pickupTimeFilter,
}: UseMapStoresOptions) {
  /** GraphQL 변수 타입은 lat/lng가 optional이라 ref는 지도 저장소 기준으로 number 고정 */
  const initialCenter = useMemo(() => getMapPosition(), []);

  const centerRef = useRef<{ lat: number; lng: number }>({
    lat: initialCenter.lat,
    lng: initialCenter.lng,
  });

  const fetchMode = availableFilter === "PICKUP_NOW" ? "pickup" : "radius";

  const [loadedOnce, setLoadedOnce] = useState(false);

  const [execJustStores, { data: justData, loading: justLoading }] =
    useLazyQuery<JustStoresQuery, JustStoresQueryVariables>(JUST_STORES);

  const [execPickupNow, { data: pickupData, loading: pickupLoading }] =
    useLazyQuery<PickupNowStoresQuery, PickupNowStoresQueryVariables>(
      GET_PICKUP_NOW_STORES,
    );

  const fetchAtCenter = useCallback(
    async (lat: number, lng: number) => {
      centerRef.current = { lat, lng };
      if (fetchMode === "pickup") {
        await execPickupNow({
          variables: {
            lat,
            lng,
            limit: MAP_PICKUP_NOW_LIMIT,
            offset: 0,
          },
        });
      } else {
        await execJustStores({
          variables: {
            lat,
            lng,
            radiusKm: DEFAULT_RADIUS_KM,
          },
        });
      }
    },
    [execJustStores, execPickupNow, fetchMode],
  );

  useEffect(() => {
    let cancelled = false;
    void fetchAtCenter(centerRef.current.lat, centerRef.current.lng).finally(
      () => {
        if (!cancelled) setLoadedOnce(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [fetchAtCenter]);

  const searchHere = useCallback(
    async (lat: number, lng: number) => {
      await fetchAtCenter(lat, lng);
    },
    [fetchAtCenter],
  );

  const rawStores = useMemo(() => {
    if (fetchMode === "pickup") {
      return (pickupData?.storesV2PickupNow ?? []).flatMap((s) =>
        s == null ? [] : [s as Store],
      );
    }
    return (justData?.justStores ?? []).flatMap((s) =>
      s == null ? [] : [s as Store],
    );
  }, [fetchMode, justData?.justStores, pickupData?.storesV2PickupNow]);

  const displayStores = useMemo(() => {
    if (availableFilter === "PICKUP_NOW") {
      return rawStores.filter((s) =>
        filterByPickupTime(s as Store, pickupTimeFilter),
      );
    }
    const pred = STORE_FILTER[availableFilter];
    return rawStores.filter((s) => {
      const store = s as Store;
      return pred(store) && filterByPickupTime(store, pickupTimeFilter);
    });
  }, [availableFilter, pickupTimeFilter, rawStores]);

  const isInitialLoading = !loadedOnce;
  const routeLoading = fetchMode === "pickup" ? pickupLoading : justLoading;
  const isRedoFetching = loadedOnce && routeLoading;

  return {
    displayStores,
    isInitialLoading,
    isRedoFetching,
    searchHere,
  };
}
